import { AlertCircle, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface OnboardingStep1Props {
  firstName: string;
  onComplete: () => void;
}

const REQUIREMENTS = [
  { label: "At least 12 characters", test: (p: string) => p.length >= 12 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { label: "One symbol", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export function OnboardingStep1({ firstName, onComplete }: OnboardingStep1Props) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allMet = REQUIREMENTS.every((r) => r.test(password));
  const mismatch = confirm.length > 0 && password !== confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allMet) {
      setError("Password does not meet all requirements.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/onboarding/step/1", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data: { error?: string } = await res.json().catch(() => ({}));
        setError(data.error ?? "Failed to set password.");
        return;
      }
      toast.success("Password set");
      onComplete();
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Welcome{firstName ? `, ${firstName}` : ""}!</h2>
        <p className="text-sm text-muted-foreground">
          Let's start by setting a secure password for your account.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-600 ring-1 ring-red-500/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ob-password">New password</Label>
          <Input
            id="ob-password"
            type="password"
            autoFocus
            placeholder="Create a strong password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ob-confirm">Confirm password</Label>
          <Input
            id="ob-confirm"
            type="password"
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className={mismatch ? "border-red-500 focus-visible:ring-red-500" : ""}
          />
          {mismatch && <p className="text-xs text-red-500">Passwords do not match.</p>}
        </div>
      </div>

      <div className="space-y-2 rounded-lg bg-muted/50 p-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Requirements
        </p>
        <ul className="space-y-1.5">
          {REQUIREMENTS.map((r) => {
            const met = r.test(password);
            return (
              <li key={r.label} className="flex items-center gap-2 text-sm">
                {met ? (
                  <CheckCircle2 className="size-4 text-green-500 shrink-0" />
                ) : (
                  <Circle className="size-4 text-muted-foreground/40 shrink-0" />
                )}
                <span className={met ? "text-foreground" : "text-muted-foreground"}>{r.label}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <Button
        type="submit"
        disabled={loading || !allMet || mismatch || !confirm}
        className="w-full"
      >
        {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
        Continue
      </Button>
    </form>
  );
}
